import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Save, Send, ExternalLink, Mail, CheckCircle2, XCircle, MessageCircle, Eye, Stethoscope } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const [s, setS] = useState(null);
  const [botToken, setBotToken] = useState("");
  const [chatId, setChatId] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [hour, setHour] = useState(9);
  const [busy, setBusy] = useState(false);
  const [diag, setDiag] = useState(null);
  const [diagBusy, setDiagBusy] = useState(false);

  async function load() {
    try {
      const { data } = await api.get("/notifications/settings");
      setS(data);
      setChatId(data.chat_id || "");
      setEnabled(data.daily_digest_enabled);
      setHour(data.daily_digest_hour_ist);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }
  useEffect(() => { load(); }, []);

  async function save() {
    setBusy(true);
    try {
      const payload = {
        chat_id: chatId,
        daily_digest_enabled: enabled,
        daily_digest_hour_ist: Number(hour) || 9,
      };
      if (botToken.trim()) payload.bot_token = botToken.trim();
      await api.post("/notifications/settings", payload);
      setBotToken("");
      toast.success("Settings saved.");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  }

  async function testPing() {
    try {
      await api.post("/notifications/test");
      toast.success("Test message sent. Check Telegram.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  async function diagnose() {
    setDiagBusy(true);
    setDiag(null);
    try {
      const { data } = await api.post("/notifications/diagnose");
      setDiag(data);
      if (data.ok) toast.success("Diagnose OK. Message sent.");
      else toast.error("Diagnose found an issue — see details below.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setDiagBusy(false); }
  }

  async function sendNow() {
    try {
      await api.post("/notifications/digest/send");
      toast.success("Digest sent.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-3xl" data-testid="settings-page">
      <header className="mb-8">
        <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500 mb-2">/ ADMIN · NOTIFICATIONS</div>
        <h1 className="section-title text-5xl lg:text-6xl">Settings.</h1>
        <p className="text-zinc-600 mt-3 text-sm max-w-xl">Wire your Telegram bot to receive a daily inventory digest at a fixed time, plus on-demand pings.</p>
      </header>

      <div className="brut-card p-6 mb-6">
        <div className="font-mono-ds text-[11px] tracking-widest uppercase mb-1">How to set this up</div>
        <ol className="list-decimal pl-5 text-sm space-y-2 text-zinc-700">
          <li>Open Telegram → search <b>@BotFather</b> → send <code className="font-mono-ds">/newbot</code> → follow steps → copy the <b>bot token</b> it gives you (looks like <code className="font-mono-ds">1234567890:ABC...</code>).</li>
          <li>To get your <b>Chat ID</b>: search <b>@userinfobot</b>, send any message — it replies with your numeric ID.<br/>
            <span className="text-xs text-zinc-500">For a group: add your bot to the group, send any message in the group, then visit <code className="font-mono-ds">https://api.telegram.org/bot&lt;TOKEN&gt;/getUpdates</code> in a browser to find <code>chat.id</code> (negative number for groups).</span></li>
          <li>Paste both below → Save → click <b>Send Test Ping</b>.</li>
        </ol>
        <a href="https://core.telegram.org/bots#how-do-i-create-a-bot" target="_blank" rel="noreferrer" className="font-mono-ds text-[11px] tracking-widest uppercase mt-3 inline-flex items-center gap-1 hover:underline">
          Telegram bot docs <ExternalLink size={12} />
        </a>
      </div>

      <div className="brut-card p-6 space-y-5">
        <Field label={`Bot Token ${s?.bot_token_set ? `(currently set: ${s.bot_token_preview})` : "(not set)"}`}>
          <input
            type="password"
            placeholder={s?.bot_token_set ? "Leave blank to keep existing" : "Paste token from @BotFather"}
            className="brut-input font-mono-ds"
            value={botToken}
            onChange={(e) => setBotToken(e.target.value)}
            data-testid="bot-token-input"
          />
        </Field>

        <Field label="Chat ID (your user ID, or group ID if pinging a group)">
          <input
            placeholder="e.g. 123456789 or -1001234567890"
            className="brut-input font-mono-ds"
            value={chatId}
            onChange={(e) => setChatId(e.target.value)}
            data-testid="chat-id-input"
          />
        </Field>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Daily Digest Enabled">
            <select className="brut-input" value={enabled ? "yes" : "no"} onChange={(e) => setEnabled(e.target.value === "yes")} data-testid="digest-enabled-select">
              <option value="yes">Yes — send daily</option>
              <option value="no">No — only on-demand</option>
            </select>
          </Field>
          <Field label="Daily Digest Hour (IST, 0-23)">
            <input type="number" min={0} max={23} className="brut-input font-mono-ds" value={hour} onChange={(e) => setHour(e.target.value)} data-testid="digest-hour-input" />
          </Field>
        </div>

        <div className="flex flex-wrap gap-3 pt-2">
          <button onClick={save} disabled={busy} className="brut-btn px-5 py-2.5 inline-flex items-center gap-2" data-testid="save-settings-btn">
            <Save size={16} /> {busy ? "Saving…" : "Save Settings"}
          </button>
          <button onClick={testPing} className="brut-btn-outline px-5 py-2.5 inline-flex items-center gap-2" data-testid="test-ping-btn">
            <Send size={16} /> Send Test Ping
          </button>
          <button onClick={diagnose} disabled={diagBusy} className="brut-btn-outline px-5 py-2.5 inline-flex items-center gap-2 disabled:opacity-50" data-testid="diagnose-telegram-btn">
            <Stethoscope size={16} /> {diagBusy ? "Diagnosing…" : "Diagnose"}
          </button>
          <button onClick={sendNow} className="brut-btn-outline px-5 py-2.5 inline-flex items-center gap-2" data-testid="send-digest-now-btn">
            <Send size={16} /> Send Digest Now
          </button>
        </div>

        {diag && (
          <div className={`brut-card-flat p-4 mt-4 ${diag.ok ? "bg-[#E7F5EE]" : "bg-[#FDF6EE]"}`} data-testid="diagnose-result">
            <div className="flex items-center gap-2 mb-2">
              {diag.ok ? <CheckCircle2 size={16} className="text-[#22A06B]" /> : <XCircle size={16} className="text-[#B34A00]" />}
              <div className="font-display font-bold text-sm">Telegram Diagnose · {diag.ok ? "OK" : "Failed"}</div>
            </div>
            {diag.hint && (
              <div className="text-sm text-zinc-800 mb-3" data-testid="diagnose-hint">{diag.hint}</div>
            )}
            <div className="space-y-1.5">
              {(diag.steps || []).map((st, i) => (
                <div key={i} className="brut-card-flat bg-white p-2.5 text-xs font-mono-ds flex items-start gap-2">
                  {st.ok ? <CheckCircle2 size={14} className="text-[#22A06B] shrink-0 mt-0.5" /> : <XCircle size={14} className="text-[#B34A00] shrink-0 mt-0.5" />}
                  <div className="flex-1 break-all">
                    <div className="font-bold uppercase tracking-widest text-[10px] mb-0.5">{st.name} · HTTP {st.http_status}</div>
                    {st.bot_username && <div>bot: @{st.bot_username} (id {st.bot_id})</div>}
                    {st.chat_title && <div>chat: {st.chat_type} · {st.chat_title}</div>}
                    {st.error && <div className="text-[#B34A00]">error: {st.error}</div>}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3 text-[11px] text-zinc-500 font-mono-ds">
              bot_token: {diag.config?.bot_token_preview || "not set"} · chat_id: {diag.config?.chat_id || "not set"}
            </div>
          </div>
        )}
      </div>

      <EmailNotificationsPanel />
      <OpsSummaryPanel />
    </div>
  );
}

function OpsSummaryPanel() {
  // Reads both the Telegram config (from top-of-page /notifications/settings)
  // and the WhatsApp config to decide what's live for the 21:30 IST digest.
  const [tg, setTg] = useState(null);
  const [wa, setWa] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);

  async function load() {
    try {
      const [a, b] = await Promise.all([
        api.get("/notifications/settings"),
        api.get("/notifications/whatsapp/status"),
      ]);
      setTg(a.data);
      setWa(b.data);
    } catch (e) {
      // ignore silently for non-admin
    }
  }
  useEffect(() => { load(); }, []);

  async function loadPreview() {
    setLoadingPreview(true);
    try {
      const { data } = await api.get("/notifications/whatsapp/summary-preview");
      setPreview(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoadingPreview(false); }
  }

  async function sendSummaryNow() {
    setBusy(true);
    try {
      const { data } = await api.post("/notifications/whatsapp/send-summary-now");
      const channels = [data?.telegram && "Telegram", data?.whatsapp && "WhatsApp"].filter(Boolean).join(" + ");
      toast.success(`Summary fired via ${channels || "no channel"}. Check your phone.`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  }

  const tgReady = !!tg?.bot_token_set && !!tg?.chat_id;
  const waReady = !!wa?.provider && wa.provider !== "none";
  const anyReady = tgReady || waReady;

  return (
    <div className="brut-card p-6 mt-6" data-testid="ops-summary-panel">
      <div className="flex items-center gap-2 mb-4">
        <MessageCircle size={18} className="text-[#22A06B]" />
        <div>
          <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500">DAILY OPS DIGEST · 21:30 IST</div>
          <div className="font-display font-black text-xl">Punch-outs + Daily Sales Summary</div>
        </div>
        <span className={`ml-auto inline-flex items-center gap-1 px-2.5 py-1 border-2 border-black font-mono-ds text-[10px] font-bold uppercase ${anyReady ? "bg-[#22A06B] text-white" : "bg-[#FFB800]"}`} data-testid="ops-summary-badge">
          {anyReady ? <><CheckCircle2 size={12} /> Live</> : <><XCircle size={12} /> Not configured</>}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <div className={`brut-card-flat p-3 ${tgReady ? "bg-[#E7F5EE]" : "bg-[#FDF6EE]"}`} data-testid="ops-telegram-status">
          <div className="flex items-center justify-between">
            <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500">Telegram (primary)</div>
            {tgReady ? <CheckCircle2 size={14} className="text-[#22A06B]" /> : <XCircle size={14} className="text-[#FFB800]" />}
          </div>
          <div className="font-display font-bold text-sm mt-1">{tgReady ? `chat_id ${tg.chat_id}` : "Set bot token + chat_id above ↑"}</div>
        </div>
        <div className={`brut-card-flat p-3 ${waReady ? "bg-[#E7F5EE]" : "bg-white"}`} data-testid="ops-whatsapp-status">
          <div className="flex items-center justify-between">
            <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500">WhatsApp (optional)</div>
            {waReady ? <CheckCircle2 size={14} className="text-[#22A06B]" /> : <XCircle size={14} className="text-zinc-400" />}
          </div>
          <div className="font-display font-bold text-sm mt-1">
            {waReady ? `${wa.provider} → ${wa.recipient}` : "Not configured"}
          </div>
        </div>
      </div>

      {!tgReady && (
        <div className="brut-card-flat p-4 bg-[#FDF6EE] mb-4">
          <div className="font-display font-bold text-sm mb-2">Set up Telegram (2 min):</div>
          <ol className="list-decimal pl-5 text-sm space-y-1.5 text-zinc-700">
            <li>Open Telegram → search <b>@BotFather</b> → send <code className="font-mono-ds bg-white px-1">/newbot</code> → follow prompts.</li>
            <li>Copy the <b>bot token</b> (looks like <code className="font-mono-ds bg-white px-1">1234567890:ABC…</code>).</li>
            <li>Get your <b>Chat ID</b>: search <b>@userinfobot</b> and send it any message.</li>
            <li>Paste both into the <b>Telegram</b> section at the top of this page → Save → click <b>Send Test Ping</b> to verify.</li>
            <li>Come back here → click <b>Preview Today's Summary</b> or <b>Send Now</b>.</li>
          </ol>
        </div>
      )}

      <div className="font-mono-ds text-[11px] tracking-widest uppercase text-zinc-600 mb-2">Every day at 21:30 IST you'll receive:</div>
      <ul className="text-sm text-zinc-700 space-y-1 mb-4">
        <li>· <b>Punch-outs</b> — every task closed today with employee, time, and work notes</li>
        <li>· <b>Daily Sales</b> — every order today with customer, items, qty, amount, and status</li>
        <li>· Sent via Telegram (primary) and WhatsApp (if configured)</li>
      </ul>

      <div className="flex flex-wrap gap-3">
        <button onClick={loadPreview} disabled={loadingPreview} className="brut-btn-outline px-5 py-2.5 inline-flex items-center gap-2 disabled:opacity-50" data-testid="ops-preview-btn">
          <Eye size={14} /> {loadingPreview ? "Loading…" : "Preview Today's Summary"}
        </button>
        <button onClick={sendSummaryNow} disabled={busy || !anyReady} className="brut-btn px-5 py-2.5 inline-flex items-center gap-2 disabled:opacity-50" data-testid="ops-send-now-btn">
          <Send size={14} /> {busy ? "Sending…" : "Send Today's Summary Now"}
        </button>
        <button onClick={load} className="brut-btn-outline px-5 py-2.5" data-testid="ops-refresh-btn">Refresh status</button>
      </div>

      {preview && (
        <div className="brut-card-flat p-4 bg-white mt-4" data-testid="ops-preview-box">
          <div className="font-mono-ds text-[11px] tracking-widest uppercase text-zinc-500 mb-2">Preview for {preview.date}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="font-display font-bold text-sm mb-1">Punch-outs · {preview.punch_outs_count}</div>
              <pre className="brut-card-flat bg-[#FDF6EE] p-3 text-xs font-mono-ds whitespace-pre-wrap max-h-48 overflow-auto">{preview.punch_outs}</pre>
            </div>
            <div>
              <div className="font-display font-bold text-sm mb-1">Daily Sales · {preview.sales_orders} orders · ₹{Math.round(preview.sales_revenue || 0).toLocaleString("en-IN")}</div>
              <pre className="brut-card-flat bg-[#FDF6EE] p-3 text-xs font-mono-ds whitespace-pre-wrap max-h-48 overflow-auto">{preview.sales}</pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function EmailNotificationsPanel() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    try {
      const { data } = await api.get("/notifications/email/status");
      setStatus(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }
  useEffect(() => { load(); }, []);

  async function testEmail() {
    setBusy(true);
    try {
      const { data } = await api.post("/notifications/email/test");
      toast.success(`Test email sent to ${(data.recipients || []).join(", ")}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  }

  const configured = status?.configured;
  return (
    <div className="brut-card p-6 mt-6" data-testid="email-panel">
      <div className="flex items-center gap-2 mb-4">
        <Mail size={18} className="text-[#7A3E1F]" />
        <div>
          <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500">GMAIL SMTP · EMAIL NOTIFICATIONS</div>
          <div className="font-display font-black text-xl">Email Alerts</div>
        </div>
        <span className={`ml-auto inline-flex items-center gap-1 px-2.5 py-1 border-2 border-black font-mono-ds text-[10px] font-bold uppercase ${configured ? "bg-[#22A06B] text-white" : "bg-[#FFB800]"}`} data-testid="email-config-badge">
          {configured ? <><CheckCircle2 size={12} /> Configured</> : <><XCircle size={12} /> Not configured</>}
        </span>
      </div>

      {!configured && (
        <div className="brut-card-flat p-4 bg-[#FDF6EE] mb-4">
          <div className="font-display font-bold text-sm mb-2">Setup steps (2 min):</div>
          <ol className="list-decimal pl-5 text-sm space-y-1.5 text-zinc-700">
            <li>Open <a href="https://myaccount.google.com/security" target="_blank" rel="noreferrer" className="underline">Google Account → Security</a> → enable <b>2-Step Verification</b>.</li>
            <li>Open <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="underline">App Passwords <ExternalLink size={11} className="inline"/></a> → create one named <code className="font-mono-ds bg-white px-1">Closet Inventory</code> → copy the 16-char password.</li>
            <li>Set these 3 keys in <code className="font-mono-ds bg-white px-1">/app/backend/.env</code> and restart backend:
              <pre className="brut-card-flat bg-white p-2 mt-1 text-xs font-mono-ds overflow-x-auto">GMAIL_SMTP_USER="you@gmail.com"
GMAIL_SMTP_APP_PASSWORD="xxxx xxxx xxxx xxxx"
MAIL_DEFAULT_RECIPIENTS="you@gmail.com"</pre>
            </li>
            <li>Return here and click <b>Send Test Email</b>.</li>
          </ol>
        </div>
      )}

      {configured && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <Stat label="Sending from" value={status?.smtp_user || "—"} />
          <Stat label="Display name" value={status?.from_name || "—"} />
          <Stat label="Recipients" value={(status?.default_recipients || []).join(", ") || "—"} />
        </div>
      )}

      <div className="font-mono-ds text-[11px] tracking-widest uppercase text-zinc-600 mb-2">Email is automatically sent for:</div>
      <ul className="text-sm text-zinc-700 space-y-1 mb-4">
        <li>· New Orders — subject includes order #, status, and total value</li>
        <li>· Task Ledger Punch-outs — includes ticket, employee, time and note</li>
        <li>· Daily Digest — low-stock alerts + yesterday's sales snapshot at 9:00 IST</li>
      </ul>

      <div className="flex flex-wrap gap-3">
        <button onClick={testEmail} disabled={busy || !configured} className="brut-btn px-5 py-2.5 inline-flex items-center gap-2 disabled:opacity-50" data-testid="send-test-email-btn">
          <Mail size={14} /> {busy ? "Sending…" : "Send Test Email"}
        </button>
        <button onClick={load} className="brut-btn-outline px-5 py-2.5" data-testid="refresh-email-status-btn">Refresh status</button>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="brut-card-flat p-3">
      <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500">{label}</div>
      <div className="font-display font-bold text-sm break-all">{value}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="font-mono-ds text-[11px] tracking-widest uppercase mb-1.5">{label}</div>
      {children}
    </label>
  );
}
